import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SmartLink } from "@/components/SmartLink";
import { useVisitor } from "@/lib/visitor";
import { useContent } from "@/lib/content-store";
import { intentById } from "@/lib/intents";
import { CHURCH, DEFAULT_SLIDE_IMAGES } from "@/lib/content";
import { API } from "@/lib/api";

const DURATION = 7000;
const CTA = "rounded-full bg-white px-7 text-[#0A3A6B] hover:bg-sky-100";

const Progress = ({ count, idx, onPick }) => (
  <div className="flex items-center gap-2" data-testid="hero-slider-progress">
    {Array.from({ length: count }).map((_, i) => (
      <button key={i} onClick={() => onPick(i)} aria-label={`Slide ${i + 1}`} data-testid={`hero-slide-dot-${i}`}
        className="h-1 w-12 overflow-hidden rounded-full bg-white/25 sm:w-16">
        <span key={i === idx ? `on-${idx}` : `off-${i}`} className={`block h-full bg-white ${i < idx ? "w-full" : "w-0"}`}
          style={i === idx ? { animation: `vp-progress ${DURATION}ms linear forwards` } : undefined} />
      </button>
    ))}
  </div>
);

const PrimaryCta = ({ it, onPrompt, onAction }) => {
  if (!it) return <Button size="lg" className={CTA} onClick={onPrompt} data-testid="hero-start-questionnaire-button">How can we serve you? <ArrowRight className="ml-1 h-4 w-4" /></Button>;
  const s = it.steps[0];
  if (s.action) return <Button size="lg" className={CTA} onClick={() => onAction(s.action)} data-testid="hero-primary-cta">{s.title} <ArrowRight className="ml-1 h-4 w-4" /></Button>;
  return <Button asChild size="lg" className={CTA} data-testid="hero-primary-cta"><SmartLink href={s.href}>{s.title} <ArrowRight className="ml-1 h-4 w-4" /></SmartLink></Button>;
};

export const Hero = () => {
  const { intent, profile, openQuestionnaire, openAction } = useVisitor();
  const content = useContent();
  const it = intentById(intent);
  const welcome = it?.audience === "member" ? content.welcome_member : content.welcome_new;
  const source = content.slides?.length ? content.slides : CHURCH.slides;
  const slides = source.slice(0, 3).map((s, i) => ({
    ...s, img: content.slide_images?.[i] ? `${API}/public/slides/${i}/image?v=${content.slide_images[i]}` : DEFAULT_SLIDE_IMAGES[i],
  }));
  const [idx, setIdx] = useState(0);
  const slide = slides[idx] || slides[0];

  useEffect(() => {
    const t = setTimeout(() => setIdx((i) => (i + 1) % slides.length), DURATION);
    return () => clearTimeout(t);
  }, [idx, slides.length]);

  return (
    <section className="grain relative h-[92svh] min-h-[620px] overflow-hidden bg-[#0A3A6B]" data-testid="hero-section">
      <AnimatePresence initial={false}>
        <motion.img key={`${idx}-${slide.img}`} src={slide.img} alt="" data-testid="hero-slide-image"
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ opacity: 0, scale: 1.14 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.4 }, scale: { duration: DURATION / 1000 + 2, ease: "linear" } }} />
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A3A6B]/95 via-[#0A3A6B]/70 to-[#0F172A]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-transparent to-transparent" />
      <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-10 pt-[96px] lg:justify-center lg:px-10 lg:pb-16">
        <div className="max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.h1 key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.7 }}
              className="text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              {slide.caption}<span className="font-serif-accent block font-medium text-sky-200">{slide.subcaption}</span>
            </motion.h1>
          </AnimatePresence>
          <div className="mt-7 max-w-xl rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-xl sm:p-6" data-testid="hero-welcome">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-200" data-testid="hero-welcome-title">{welcome.title}</p>
            <p className="mt-2 text-sm leading-relaxed text-white/90 sm:text-base" data-testid="hero-welcome-message">
              {profile?.nickname && <span className="font-semibold text-white">Hi {profile.nickname}! </span>}{welcome.message}
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <PrimaryCta it={it} onPrompt={openQuestionnaire} onAction={openAction} />
            <Button asChild size="lg" variant="ghost" className="rounded-full border border-white/30 px-6 text-white hover:bg-white/10 hover:text-white" data-testid="hero-worship-link">
              <SmartLink href="/worship">Worship Services</SmartLink>
            </Button>
          </div>
        </div>
        <div className="mt-10 flex items-center justify-between gap-4">
          <Progress count={slides.length} idx={idx} onPick={setIdx} />
          <span className="hidden items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70 sm:flex"><Play className="h-3 w-3 fill-current" /> {idx + 1} / {slides.length}</span>
        </div>
      </div>
    </section>
  );
};
